import { useEffect, useState } from 'react'

const CACHE_KEY = 'gh_contrib_notshekhar_v1'
const TTL_MS = 6 * 60 * 60 * 1000
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function buildWeeks(days) {
  const weeks = []
  let week = null
  days.forEach(d => {
    const dow = new Date(d.date + 'T00:00:00').getDay()
    if (!week || dow === 0) {
      week = []
      weeks.push(week)
      if (weeks.length === 1 && dow !== 0) {
        for (let i = 0; i < dow; i++) week.push(null)
      }
    }
    week.push(d)
  })
  const last = weeks[weeks.length - 1]
  if (last) while (last.length < 7) last.push(null)
  return weeks
}

function buildMonthLabels(weeks) {
  const labels = []
  let prevMonth = -1
  weeks.forEach((week, i) => {
    const first = week.find(Boolean)
    if (!first) return
    const month = new Date(first.date + 'T00:00:00').getMonth()
    if (month !== prevMonth) {
      prevMonth = month
      labels.push({ week: i, label: MONTHS[month] })
    }
  })
  if (labels.length > 1 && labels[1].week - labels[0].week < 3) labels.shift()
  return labels
}

function formatDay(d) {
  const date = new Date(d.date + 'T00:00:00')
  const nice = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  if (d.count === 0) return `No contributions on ${nice}`
  return `${d.count} contribution${d.count === 1 ? '' : 's'} on ${nice}`
}

const SKELETON_WEEKS = Array.from({ length: 53 }, () => Array.from({ length: 7 }, () => null))

function ContributionGraph({ username = 'notshekhar' }) {
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (raw) {
        const cached = JSON.parse(raw)
        if (cached && Date.now() - cached.ts < TTL_MS) {
          setData(cached.data)
          return
        }
      }
    } catch {}

    fetch(`https://github-contributions-api.jogruber.de/v4/${username}?y=last`)
      .then(r => (r.ok ? r.json() : null))
      .then(json => {
        if (cancelled) return
        if (!json || !Array.isArray(json.contributions)) {
          setFailed(true)
          return
        }
        const slim = {
          total: json.total?.lastYear ?? null,
          days: json.contributions.map(d => ({ date: d.date, count: d.count, level: d.level })),
        }
        setData(slim)
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data: slim, ts: Date.now() }))
        } catch {}
      })
      .catch(() => { if (!cancelled) setFailed(true) })

    return () => { cancelled = true }
  }, [username])

  // Third-party image as a fallback if the contributions API is unreachable
  if (failed && !data) {
    return (
      <div className="gh-card gh-contrib">
        <div className="gh-card-title">Contributions — last year</div>
        <img
          src={`https://ghchart.rshah.org/216e39/${username}`}
          alt={`${username} GitHub contribution graph`}
          className="gh-contrib-img"
          loading="lazy"
        />
      </div>
    )
  }

  const weeks = data ? buildWeeks(data.days) : SKELETON_WEEKS
  const labels = data ? buildMonthLabels(weeks) : []

  return (
    <div className="gh-card gh-contrib">
      <div className="gh-contrib-header">
        <div className="gh-card-title">Contributions — last year</div>
        {data?.total != null && (
          <span className="gh-contrib-total">{data.total.toLocaleString()} total</span>
        )}
      </div>
      <div className="gh-contrib-scroll">
        <div
          className="contrib-months"
          style={{ gridTemplateColumns: `repeat(${weeks.length}, var(--contrib-cell))` }}
        >
          {labels.map(l => (
            <span key={l.week} style={{ gridColumnStart: l.week + 1 }}>{l.label}</span>
          ))}
        </div>
        <div className={`contrib-grid ${data ? '' : 'contrib-skeleton'}`}>
          {weeks.flat().map((d, i) =>
            d ? (
              <span key={i} className={`contrib-cell l${d.level}`} title={formatDay(d)} />
            ) : (
              <span key={i} className="contrib-cell contrib-empty" aria-hidden="true" />
            )
          )}
        </div>
      </div>
      <div className="contrib-legend" aria-hidden="true">
        <span>Less</span>
        <span className="contrib-cell l0" />
        <span className="contrib-cell l1" />
        <span className="contrib-cell l2" />
        <span className="contrib-cell l3" />
        <span className="contrib-cell l4" />
        <span>More</span>
      </div>
    </div>
  )
}

export default ContributionGraph
